import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UpdateOwnPasswordData, Usuario } from '@/auth';

interface UserProfileChangePasswordDialogProps {
  open: boolean;
  user: Usuario | null;
  onClose: () => void;
  onSave: (passwordData: UpdateOwnPasswordData) => Promise<void>;
}

const INITIAL_FORM_DATA: UpdateOwnPasswordData = {
  current_password: '',
  password: '',
  password_confirmation: '',
};

const UserProfileChangePasswordDialog: React.FC<UserProfileChangePasswordDialogProps> = ({
  open,
  user,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<UpdateOwnPasswordData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<Partial<Record<keyof UpdateOwnPasswordData, string>>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
      setSaving(false);
    }
  }, [open]);

  const validateForm = (): boolean => {
    const nextErrors: Partial<Record<keyof UpdateOwnPasswordData, string>> = {};

    if (!formData.current_password) {
      nextErrors.current_password = 'Ingresá tu contraseña actual';
    }

    if (!formData.password || formData.password.length < 8) {
      nextErrors.password = 'La nueva contraseña debe tener al menos 8 caracteres';
    }

    if (!formData.password_confirmation) {
      nextErrors.password_confirmation = 'Confirmá la nueva contraseña';
    } else if (formData.password !== formData.password_confirmation) {
      nextErrors.password_confirmation = 'Las contraseñas no coinciden';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleInputChange = (field: keyof UpdateOwnPasswordData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((previous) => ({ ...previous, [field]: event.target.value }));

    if (errors[field]) {
      setErrors((previous) => ({ ...previous, [field]: undefined }));
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      await onSave(formData);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cambiar contraseña</DialogTitle>
          <DialogDescription>
            Actualizá la contraseña de {user?.email ?? 'tu cuenta'} sin cerrar la sesión actual.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current_password">Contraseña actual</Label>
            <Input
              id="current_password"
              type="password"
              value={formData.current_password}
              onChange={handleInputChange('current_password')}
              placeholder="Ingresá tu contraseña actual"
              disabled={saving}
              className={errors.current_password ? 'border-red-500' : ''}
            />
            {errors.current_password ? (
              <p className="text-sm text-red-500">{errors.current_password}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Nueva contraseña</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange('password')}
              placeholder="Ingresá la nueva contraseña"
              disabled={saving}
              className={errors.password ? 'border-red-500' : ''}
            />
            {errors.password ? (
              <p className="text-sm text-red-500">{errors.password}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password_confirmation">Confirmar nueva contraseña</Label>
            <Input
              id="password_confirmation"
              type="password"
              value={formData.password_confirmation}
              onChange={handleInputChange('password_confirmation')}
              placeholder="Repetí la nueva contraseña"
              disabled={saving}
              className={errors.password_confirmation ? 'border-red-500' : ''}
            />
            {errors.password_confirmation ? (
              <p className="text-sm text-red-500">{errors.password_confirmation}</p>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            loading={saving}
            disabled={saving}
          >
            Guardar contraseña
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileChangePasswordDialog;
